import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cn } from '@/lib/utils';

/**
 * Mid-century Checkbox — Radix-backed boolean control with the same
 * boundary contract as Input/Select (charcoal 1.5px, orange focus, no
 * layered ring). Native <input type="checkbox"> is not used because the
 * tick mark, indeterminate state, and focus chrome can't be styled
 * consistently across browsers.
 *
 * Usage:
 *   <Checkbox checked={isOn} onCheckedChange={setIsOn} aria-label="Auto-send" />
 *   <Checkbox checked={value} onCheckedChange={(v) => setValue(v === true)} />
 *
 * Note: onCheckedChange receives `boolean | "indeterminate"` per Radix.
 * Coerce to boolean (`v === true`) when storing in a primitive boolean.
 */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      'peer h-[18px] w-[18px] shrink-0 rounded-[4px] border-[1.5px] border-charcoal bg-cream',
      'transition-colors',
      'focus-visible:outline-none focus-visible:border-orange',
      'data-[state=checked]:bg-charcoal data-[state=checked]:text-cream',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
      <svg
        viewBox="0 0 12 10"
        width="10"
        height="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M1 5l3.5 3.5L11 1" />
      </svg>
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
